import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FiClock, FiHeart, FiStar, FiAward, FiCoffee } from 'react-icons/fi';
import { IoCalendarOutline, IoCheckmarkCircleOutline } from 'react-icons/io5';
import { HiOutlineLightBulb, HiOutlineSparkles } from 'react-icons/hi2';
import './About.css';

const FadeInWhenVisible = ({ children, direction = 'up', delay = 0 }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const variants = {
    hidden: { 
      opacity: 0, 
      y: direction === 'up' ? 50 : direction === 'down' ? -50 : 0,
      x: direction === 'left' ? 50 : direction === 'right' ? -50 : 0 
    },
    visible: { 
      opacity: 1, 
      y: 0, 
      x: 0,
      transition: { duration: 0.8, delay, ease: 'easeOut' } 
    }
  };

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      variants={variants}
    >
      {children}
    </motion.div>
  );
};

const About = () => {
  const experiences = [
    {
      icon: <IoCalendarOutline />,
      title: 'Seamless Planning',
      desc: 'From initial consultation to the final wrap-up, our dedicated event managers ensure your celebration is flawlessly executed.'
    },
    {
      icon: <FiAward />,
      title: 'Exquisite Decor',
      desc: 'Our design team transforms spaces into magical settings, tailoring every floral arrangement and draping to your vision.'
    },
    {
      icon: <FiCoffee />,
      title: 'Culinary Excellence',
      desc: 'Savor a gastronomic journey with our in-house catering, offering authentic regional flavors and international cuisines.'
    },
    {
      icon: <HiOutlineSparkles />,
      title: 'Memorable Moments',
      desc: 'We focus on the little details that create a big impact, ensuring your guests leave with memories to cherish forever.'
    }
  ];

  const timelineEvents = [
    { year: '2010', desc: 'Foundation Laid' },
    { year: '2012', desc: 'Construction Completed' },
    { year: '2013', desc: 'Grand Opening' },
    { year: '2015', desc: 'Expanded Dining Hall' },
    { year: '2018', desc: 'Modernization & AC Upgrade' },
    { year: '2020', desc: '500th Event Milestone' },
    { year: '2024', desc: 'Digital Transformation' }
  ];

  const values = [
    {
      icon: <FiStar />,
      title: 'Excellence',
      desc: 'We strive for perfection in every detail, ensuring the highest standards of quality and service in everything we do.'
    },
    {
      icon: <FiHeart />,
      title: 'Tradition',
      desc: 'Deeply rooted in Kerala hospitality, we honor cultural heritage while embracing contemporary elegance.'
    },
    {
      icon: <HiOutlineLightBulb />,
      title: 'Innovation',
      desc: 'Continuously evolving our facilities and services to offer modern, world-class event experiences.'
    }
  ];

  return (
    <div className="about-page">
      {/* Page Hero */}
      <section className="about-hero">
        <div className="about-hero-content">
          <motion.h1 
            className="about-hero-title"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
          >
            Our Story
          </motion.h1>
          <motion.h2 
            className="about-hero-subtitle"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5 }}
          >
            A Legacy of Grand Celebrations
          </motion.h2>
        </div>
      </section>

      {/* Chapter 1: The Vision */}
      <section className="chapter-section vision-section">
        <div className="vision-image">
          <FadeInWhenVisible direction="right">
            <img 
              src="/assets/main13.png" 
              alt="Vision of Prasannalakshmi Kalyana Mandapam & Auditorium" 
            />
          </FadeInWhenVisible>
        </div>
        <div className="vision-content">
          <FadeInWhenVisible direction="left">
            <h2 className="chapter-title">The Vision</h2>
            <div className="gold-divider"></div>
            <p className="chapter-text">
              Conceived with a dream to bring world-class celebration spaces to Kanjikode, Kerala, Prasannalakshmi Kalyana Mandapam & Auditorium Kalyana Mandapam & Auditorium was built to be more than just a venue. It was envisioned as a landmark of grandeur where families, friends, and colleagues could gather to create lifelong memories.
            </p>
            <p className="chapter-text">
              We recognized the need for a premium, accessible, and comprehensive event space in Palakkad. Today, Prasannalakshmi Kalyana Mandapam & Auditorium stands as a testament to that vision—a prestigious destination where every celebration is elevated to an art form.
            </p>
          </FadeInWhenVisible>
        </div>
      </section>

      {/* Chapter 2: The Architecture */}
      <section className="architecture-section">
        <div className="architecture-content">
          <FadeInWhenVisible>
            <h2 className="architecture-title">The Architecture</h2>
            <p className="architecture-text">
              Spanning over a majestic 50,000+ sq.ft, Prasannalakshmi Kalyana Mandapam & Auditorium is an architectural marvel that beautifully blends traditional Kerala aesthetics with contemporary design. Our grand pillars, towering ceilings, and expansive halls exude regal charm. Large, strategically placed windows allow natural lighting to flood the interiors, creating an airy, welcoming ambiance that serves as a stunning canvas for your events.
            </p>
          </FadeInWhenVisible>
        </div>
      </section>

      {/* Chapter 3: The Hospitality */}
      <section className="chapter-section hospitality-section">
        <div className="hospitality-content">
          <FadeInWhenVisible direction="right">
            <h2 className="chapter-title">The Hospitality</h2>
            <div className="gold-divider"></div>
            <p className="chapter-text">
              At the heart of Prasannalakshmi Kalyana Mandapam & Auditorium is our unwavering commitment to warm, personalized hospitality. Our dedicated event management team works tirelessly behind the scenes, ensuring that every aspect of your celebration is flawless.
            </p>
            <p className="chapter-text">
              From curating breathtaking flower arrangements and setting the perfect ambient lighting, to serving delectable feasts through our premium in-house catering, our team anticipates your needs so you can simply focus on enjoying your special day.
            </p>
          </FadeInWhenVisible>
        </div>
        <div className="hospitality-image">
          <FadeInWhenVisible direction="left">
            <img 
              src="/assets/main10.png" 
              alt="Hospitality at Prasannalakshmi Kalyana Mandapam & Auditorium" 
            />
          </FadeInWhenVisible>
        </div>
      </section>

      {/* Chapter 4: The Experience */}
      <section className="chapter-section experience-section">
        <FadeInWhenVisible>
          <h2 className="chapter-title">The Experience</h2>
          <div className="gold-divider mx-auto"></div>
          <p className="chapter-text mx-auto" style={{ maxWidth: '800px', margin: '0 auto' }}>
            We don't just host events; we craft experiences. Every element is designed to provide you and your guests with unparalleled comfort and joy.
          </p>
        </FadeInWhenVisible>
        
        <div className="experience-grid">
          {experiences.map((exp, index) => (
            <FadeInWhenVisible key={index} delay={index * 0.2}>
              <div className="experience-card">
                <div className="experience-icon">{exp.icon}</div>
                <h3 className="experience-card-title">{exp.title}</h3>
                <p className="experience-card-text">{exp.desc}</p>
              </div>
            </FadeInWhenVisible>
          ))}
        </div>
      </section>

      {/* Chapter 5: The Celebration (Magazine Style) */}
      <section className="celebration-section">
        <div className="magazine-layout">
          <motion.img 
            src="https://images.unsplash.com/photo-1519225421980-715cb0215aed?q=80&w=800&auto=format&fit=crop" 
            alt="Celebration 1" 
            className="mag-img mag-img-1"
            initial={{ opacity: 0, x: -50, rotate: -5 }}
            whileInView={{ opacity: 1, x: 0, rotate: -2 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          />
          <motion.img 
            src="/assets/host3.png" 
            alt="Celebration 2" 
            className="mag-img mag-img-2"
            initial={{ opacity: 0, x: 50, rotate: 5 }}
            whileInView={{ opacity: 1, x: 0, rotate: 2 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          />
          <motion.img 
            src="https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?q=80&w=800&auto=format&fit=crop" 
            alt="Celebration 3" 
            className="mag-img mag-img-3"
            initial={{ opacity: 0, y: -50 }}
            whileInView={{ opacity: 1, y: 0, rotate: 4 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
          />
          
          <FadeInWhenVisible delay={0.6}>
            <div className="mag-quote-box">
              <p className="mag-quote">
                "Every celebration at Prasannalakshmi Kalyana Mandapam & Auditorium is a story waiting to be told."
              </p>
            </div>
          </FadeInWhenVisible>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="chapter-section timeline-section">
        <FadeInWhenVisible>
          <h2 className="chapter-title">Our Journey</h2>
          <div className="gold-divider mx-auto"></div>
        </FadeInWhenVisible>
        
        <div className="timeline-container">
          {timelineEvents.map((event, index) => (
            <div key={index} className={`timeline-item ${index % 2 === 0 ? 'timeline-left' : 'timeline-right'}`}>
              <FadeInWhenVisible direction={index % 2 === 0 ? 'right' : 'left'} delay={index * 0.1}>
                <div className="timeline-content">
                  <div className="timeline-year">{event.year}</div>
                  <div className="timeline-desc">{event.desc}</div>
                </div>
              </FadeInWhenVisible>
            </div>
          ))}
        </div>
      </section>

      {/* Our Values */}
      <section className="chapter-section values-section">
        <FadeInWhenVisible>
          <h2 className="chapter-title values-title">Our Values</h2>
          <div className="gold-divider mx-auto" style={{ backgroundColor: 'var(--champagne)' }}></div>
        </FadeInWhenVisible>
        
        <div className="values-grid">
          {values.map((value, index) => (
            <FadeInWhenVisible key={index} delay={index * 0.2}>
              <div className="value-card">
                <div className="value-icon">{value.icon}</div>
                <h3 className="value-title">{value.title}</h3>
                <p className="value-desc">{value.desc}</p>
              </div>
            </FadeInWhenVisible>
          ))}
        </div>
      </section>

      {/* Meet Our Promise CTA */}
      <section className="about-cta-section">
        <FadeInWhenVisible>
          <h2 className="cta-title">Your Celebration, Our Promise</h2>
          <button className="cta-button">Book Your Event</button>
        </FadeInWhenVisible>
      </section>
    </div>
  );
};

export default About;
